import { useEffect, useState } from 'react';

import campaignGraphqlManager from '../../apis/campaignGraphqlManager';
import { openToastAlert } from '../../common/ToastAlert/toastAlert';
import type { AssetType } from '../../configs';
import { assetTypes } from '../../configs';

export type UseAssetProps = {
  url: string;
  assetType?: AssetType | null;
};

export type UseAsset = {
  assetUrl: string;
  hasError: boolean;
};

type CacheEntry = {
  objectUrl: string;
  // Number of mounted hook instances currently showing this object URL. Entries in use are never revoked.
  refCount: number;
};

const MAX_CACHED_ASSETS = 50;

// Map insertion order doubles as LRU order: the first key is the least recently used.
const assetCache = new Map<string, CacheEntry>();
const inFlightRequests = new Map<string, Promise<CacheEntry>>();

const evictUnusedEntries = (): void => {
  // eslint-disable-next-line no-restricted-syntax
  for (const [key, entry] of assetCache) {
    if (assetCache.size <= MAX_CACHED_ASSETS) {
      return;
    }
    if (entry.refCount === 0) {
      URL.revokeObjectURL(entry.objectUrl);
      assetCache.delete(key);
    }
  }
};

const touchEntry = (url: string, entry: CacheEntry): void => {
  assetCache.delete(url);
  assetCache.set(url, entry);
};

const fetchAsset = (url: string): Promise<CacheEntry> => {
  const cached = assetCache.get(url);
  if (cached) {
    touchEntry(url, cached);
    return Promise.resolve(cached);
  }

  const inFlight = inFlightRequests.get(url);
  if (inFlight) {
    return inFlight;
  }

  const request = campaignGraphqlManager
    .get(url, { responseType: 'blob' })
    .then(({ data }) => {
      const entry: CacheEntry = { objectUrl: URL.createObjectURL(data), refCount: 0 };
      assetCache.set(url, entry);
      return entry;
    })
    .finally(() => {
      inFlightRequests.delete(url);
    });

  inFlightRequests.set(url, request);

  return request;
};

/** Revokes every cached object URL and empties the cache. Intended for tests. */
export const clearAssetCache = (): void => {
  assetCache.forEach(({ objectUrl }) => URL.revokeObjectURL(objectUrl));
  assetCache.clear();
  inFlightRequests.clear();
};

export const ASSET_ERROR_TOAST_KEY = 'asset-fetch-error';
export const ASSET_ERROR_TOAST_WINDOW_MS = 5000;

// Shared across all useAsset instances so that many simultaneous failures surface as a single toast
let lastAssetErrorToastAt: number | null = null;

export const resetAssetErrorToast = (): void => {
  lastAssetErrorToastAt = null;
};

const openAssetErrorToast = (): void => {
  const now = Date.now();
  if (lastAssetErrorToastAt !== null && now - lastAssetErrorToastAt < ASSET_ERROR_TOAST_WINDOW_MS) return;

  lastAssetErrorToastAt = now;
  openToastAlert({ alertType: 'error', message: 'Failed to load asset previews', key: ASSET_ERROR_TOAST_KEY });
};

const useAsset = ({ url, assetType }: UseAssetProps): UseAsset => {
  const [assetUrl, setAssetUrl] = useState<string>(() => assetCache.get(url)?.objectUrl ?? '');
  const [hasError, setHasError] = useState<boolean>(false);

  useEffect(() => {
    if (assetType === assetTypes.TextAsset.key) {
      return undefined;
    }

    let isActive = true;
    let acquiredEntry: CacheEntry | undefined;

    setHasError(false);

    fetchAsset(url)
      .then((entry) => {
        if (!isActive) {
          return;
        }
        acquiredEntry = entry;
        entry.refCount += 1;
        setAssetUrl(entry.objectUrl);
        evictUnusedEntries();
      })
      .catch(() => {
        if (isActive) {
          setHasError(true);
        }
        openAssetErrorToast();
      });

    return (): void => {
      isActive = false;
      if (acquiredEntry) {
        acquiredEntry.refCount -= 1;
        evictUnusedEntries();
      }
    };
  }, [url, assetType]);

  return {
    assetUrl,
    hasError,
  };
};

export default useAsset;
